import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DoorOpen } from 'lucide-react';
import type { CheckInArrival, ManagedCheckIn, PublicBookingResult } from '@rx-manager/shared';
import { BookingApiError, bookingApi, checkInApi } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { Alert, Button, CHECKBOX, IconTile } from '../../components/ui';
import {
  ArrivalStatus,
  TextWhenCalledBox,
  checkInFailure,
  clockTime,
  failureText,
  ticketsOf,
  tile,
  type Failure,
} from './shared';

/**
 * Track CIQ (CIQ-D6) — "I'm here" on the manage page, for an in-person visit today while check-in
 * is on. The server says where it stands (`checkIn`; absent from an older backend, and then the
 * page shows nothing of it): not open yet, open, checked in, or closed.
 *
 * A party chooses who is here, by first name; everyone still on its time is ticked. "Text me when
 * it's my turn" is offered where the pharmacy can text the mobile on file, in the server's words.
 * Once they are in line the card gives their number and their place in line, and the booking is
 * read again.
 */

/** Where the card stands: the server's, or what a refusal just said. */
type Stand = ManagedCheckIn['state'] | 'unavailable';

export function ManageCheckIn({
  token,
  booking,
  checkIn,
  onBooking,
}: {
  token: string;
  booking: PublicBookingResult;
  checkIn: ManagedCheckIn;
  onBooking: (next: PublicBookingResult) => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  /** Track CGS — a party's people who can still check in: on its time, booked or a no-show. */
  const people = useMemo(
    () =>
      booking.party
        ? booking.party.people
            .filter((person) => person.together && (person.status === 'booked' || person.status === 'no_show'))
            .sort((a, b) => a.position - b.position)
        : null,
    [booking.party],
  );
  const [picked, setPicked] = useState<number[]>(() => (people ?? []).map((person) => person.position));
  const [texts, setTexts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arrival, setArrival] = useState<CheckInArrival | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  /** A refusal that moved the card on: too early (with when it opens), too late, or not here. */
  const [refused, setRefused] = useState<{ stand: Stand; opensAt?: string } | null>(null);

  const stand: Stand = refused?.stand ?? checkIn.state;
  const opensAt = clockTime(refused?.opensAt ?? checkIn.opensAt, booking.location.timezone, locale);

  const arrive = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const result = await checkInApi.manageArrive(token, {
        // A party says who is here; one person's link needs no list.
        ...(people ? { people: picked } : {}),
        textWhenCalled: checkIn.textWhenCalled && texts,
        language: locale,
      });
      setArrival(result);
      setRefused(null);
      // The visit is checked in now: read it again for the rest of the page.
      void bookingApi
        .managed(token)
        .then(onBooking)
        .catch(() => undefined);
    } catch (error) {
      if (error instanceof BookingApiError) {
        if (error.code === 'too_early') {
          const at = typeof error.extra.opensAt === 'string' ? error.extra.opensAt : undefined;
          setRefused({ stand: 'not_yet', opensAt: at });
          return;
        }
        if (error.code === 'too_late') {
          setRefused({ stand: 'closed' });
          return;
        }
        if (error.code === 'check_in_unavailable') {
          setRefused({ stand: 'unavailable' });
          return;
        }
      }
      setFailure(checkInFailure(error));
    } finally {
      setBusy(false);
    }
  };

  const follow = (queueToken: string): void => {
    // Their place in line, in the language of this page; the token only ever in its address.
    navigate(`/q/${encodeURIComponent(queueToken)}`, { state: { lang: locale } });
  };

  const followButton = (queueToken: string | null) =>
    queueToken && (
      <Button onClick={() => follow(queueToken)} className="w-full sm:w-auto">
        {t('checkIn.follow')}
      </Button>
    );

  const line =
    stand === 'not_yet'
      ? t('checkIn.tooEarly', { time: opensAt })
      : stand === 'open'
        ? t('checkIn.manageLead')
        : stand === 'checked_in'
          ? t('checkIn.checkedIn')
          : stand === 'closed'
            ? t('checkIn.tooLate')
            : t('checkIn.unavailable');

  return (
    <section
      aria-labelledby="check-in-heading"
      className="space-y-4 rounded-xl border border-primary/25 bg-accent/50 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <IconTile icon={DoorOpen} className="bg-card" />
        <div className="min-w-0 space-y-1">
          <h2 id="check-in-heading" className="text-lg font-semibold text-heading">
            {t('checkIn.manageHeading')}
          </h2>
          {!arrival && (
            <p role="status" className="text-base text-foreground">
              {line}
            </p>
          )}
        </div>
      </div>

      {failure && <Alert>{failureText(failure, t)}</Alert>}

      {arrival ? (
        <div className="space-y-4 rounded-xl bg-card p-4 sm:p-5">
          <ArrivalStatus
            tickets={ticketsOf(arrival)}
            ahead={arrival.ahead}
            lead={<p className="text-lg font-semibold text-heading">{t('checkIn.checkedIn')}</p>}
          />
          {followButton(arrival.queueToken)}
        </div>
      ) : stand === 'open' ? (
        <div className="space-y-4">
          {people && people.length > 0 && (
            <fieldset className="space-y-3">
              <legend className="text-base font-semibold text-heading">{t('checkIn.whoIsHere')}</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {people.map((person) => (
                  <label key={person.position} className={tile(picked.includes(person.position))}>
                    <input
                      type="checkbox"
                      checked={picked.includes(person.position)}
                      onChange={() =>
                        setPicked((current) =>
                          current.includes(person.position)
                            ? current.filter((position) => position !== person.position)
                            : [...current, person.position].sort((a, b) => a - b),
                        )
                      }
                      className={CHECKBOX}
                    />
                    <span className="text-base font-medium text-heading">{person.firstName}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {checkIn.textWhenCalled && (
            <TextWhenCalledBox checked={texts} onChange={setTexts} consent={checkIn.consent} />
          )}
          <Button
            onClick={() => void arrive()}
            loading={busy}
            disabled={people !== null && picked.length === 0}
            className="w-full sm:w-auto"
          >
            <DoorOpen className="h-4 w-4" aria-hidden="true" />
            {t('checkIn.imHere')}
          </Button>
        </div>
      ) : stand === 'checked_in' ? (
        followButton(checkIn.queueToken)
      ) : null}
    </section>
  );
}
